/**
 * Remise anti-gaspi PAR LOT — pilotage (page Promos DLC courte).
 *
 * Liste les lots dont la date approche (ou déjà remisés) et permet de poser un
 * taux sur UN lot, sans toucher aux autres lots du même produit. À la caisse,
 * le scan du produit ouvre alors une fenêtre : lot remisé (avec sa date) ou
 * prix normal — le caissier regarde la date sur l'article. Pas d'étiquette.
 *
 * La remise s'éteint toute seule quand le lot est épuisé ou que sa date passe.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { formatPrice, formatQty } from '../utils/pos.utils';
import { logger } from '../utils/logger';
import ControlesRemiseLot from './remise/ControlesRemiseLot';

interface LotCandidat {
  lot_id: string;
  lot_number: string;
  product_id: string;
  product_name: string;
  category_name: string | null;
  unit: string | null;
  is_weighted: boolean;
  expiry_date: string | null;
  days_left: number | null;
  stock_display: number;
  stock_backroom: number;
  lots_in_stock: number;
  price_cents: number | null;
  promo_percent: number | null;
  promo_price_cents: number | null;
  product_promo_percent: number | null;
}

function dateFr(iso: string | null): string {
  if (!iso) return 'sans date';
  const [a, m, j] = String(iso).slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

export default function RemiseParLot() {
  const [jours, setJours] = useState(7);
  const [lots, setLots] = useState<LotCandidat[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const data: LotCandidat[] = await api.get(`/lots/promo-candidates?days=${jours}`);
      setLots(Array.isArray(data) ? data : []);
    } catch (e) {
      logger.error('[RemiseParLot] chargement échoué:', e);
      setErreur('Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [jours]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const remises = lots.filter((l) => l.promo_percent);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-800">Remise par lot ({remises.length} en cours)</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Remise UN lot sans toucher aux autres. Au scan, la caisse demande quel lot le client a en main.
          </p>
        </div>
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Date dans
          <select
            value={jours}
            onChange={(e) => setJours(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {[3, 7, 14, 30].map((j) => (
              <option key={j} value={j}>
                {j} jours
              </option>
            ))}
          </select>
        </label>
      </div>

      {message && <p className="px-4 pt-3 text-sm font-medium text-emerald-700">{message}</p>}
      {erreur && <p className="px-4 pt-3 text-sm font-medium text-red-700">{erreur}</p>}

      {loading ? (
        <p className="px-4 py-6 text-sm text-gray-500">Chargement…</p>
      ) : lots.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">Aucun lot dont la date tombe dans les {jours} jours.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {lots.map((l) => {
            const urgent = l.days_left != null && l.days_left <= 2;
            const unite = l.is_weighted || l.unit === 'kg' ? '/kg' : '';
            return (
              <li key={l.lot_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{l.product_name}</div>
                  <div className="text-xs text-gray-500">
                    {l.category_name ? `${l.category_name} · ` : ''}lot {l.lot_number}
                    {l.lots_in_stock > 1 ? ` · ${l.lots_in_stock} lots en stock` : ' · seul lot en stock'}
                  </div>
                  <div className="text-sm mt-0.5">
                    <span className={urgent ? 'font-bold text-red-700' : 'text-gray-700'}>
                      Date {dateFr(l.expiry_date)}
                      {l.days_left != null ? ` (${l.days_left <= 0 ? "aujourd'hui" : `dans ${l.days_left} j`})` : ''}
                    </span>
                    <span className="text-gray-500">
                      {' '}· rayon {formatQty(l.stock_display)} · réserve {formatQty(l.stock_backroom)}
                    </span>
                    {l.price_cents != null && (
                      <span className="text-gray-500"> · {formatPrice(l.price_cents)}{unite}</span>
                    )}
                  </div>
                  {l.product_promo_percent ? (
                    <div className="text-xs text-amber-700 mt-0.5">
                      Promo de fiche −{l.product_promo_percent} % déjà posée sur ce produit : elle prime.
                    </div>
                  ) : null}
                </div>

                <ControlesRemiseLot
                  lotId={l.lot_id}
                  promoPercent={l.promo_percent}
                  prixCents={l.price_cents}
                  unite={unite}
                  onChange={(msg, err) => {
                    setMessage(msg ? `« ${l.product_name} » (date ${dateFr(l.expiry_date)}) : ${msg}` : null);
                    setErreur(err ?? null);
                    if (msg) void charger();
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
